import { Button, Dropdown, Space } from "antd";
import { Children, cloneElement, isValidElement, type ReactElement, type ReactNode } from "react";
import { Tag, Table as ZhTable } from "zhihao-ui-react";
import type { ColumnProps, RenderScope } from "@/views/components/ProTable/interface";
import { filterEnum, filterEnumObj, formatValue, handleRowAccordingToProp } from "@/utils";

export interface TableColumnProps<T extends Record<string, any> = Record<string, any>> {
    column: ColumnProps<T>;
    enumMap?: Map<string, any[]>;
    operation?: ReactNode;
}

/**
 * 按 Vue TableColumn 的约定收拢过多操作：保留前两个动作，其余动作放入“更多”。
 * 仅处理操作列的直接子节点，避免改变普通单元格和已有自定义下拉菜单。
 */
export function normalizeOperationActions(content: ReactNode): ReactNode {
    // React render 函数既可能返回包裹节点，也可能直接返回动作数组；两种形态都要遵循 Vue 的操作列折叠规则。
    const actionSource = isValidElement(content)
        ? (content.props as { children?: ReactNode }).children
        : Array.isArray(content)
          ? content
          : undefined;
    if (actionSource === undefined) return content;

    const children = Children.toArray(actionSource);
    if (children.length <= 3) return content;

    const overflowItems = children.slice(2).map((child, index) => ({
        key: `operation-${index}`,
        label: child
    }));
    const visibleActions = [
        ...children.slice(0, 2),
        <Dropdown key="operation-more" trigger={["click"]} menu={{ items: overflowItems }}>
            <Button className="operation-more-action" type="link">
                更多
            </Button>
        </Dropdown>
    ];

    if (isValidElement(content)) {
        return cloneElement(content as ReactElement<{ children?: ReactNode }>, { children: visibleActions });
    }

    // 直接数组没有可复用的宿主节点，补充与页面现有操作列一致的紧凑布局容器。
    return (
        <Space size={0} wrap>
            {visibleActions}
        </Space>
    );
}

/**
 * 为直接使用 zhihao Table 的页面复用 Vue 操作列溢出规则。
 * ProTable 会自动经过 TableColumn 适配层，但页面直传 columns 时也必须显式接入同一契约。
 */
export function normalizeOperationColumns<T extends Record<string, any>>(columns: T[]): T[] {
    return columns.map(column => {
        const prop = String(column.prop ?? column.key ?? "");
        if (!column.render || (prop !== "operation" && prop !== "operations" && column.label !== "操作")) {
            return column;
        }

        // Vue TableColumn 会根据操作文案动态扩容固定列；React 表格列宽是静态的，至少预留三项链接动作的空间。
        const declaredWidth = Number(column.width);
        const compactOperation = String(column.className ?? "").includes("compact");
        // 供应商旧 Vue 配置保留 240px 声明，但双链接在旧表格中按内容实际收缩到 134px。
        const supplierOperation = String(column.className ?? "").includes("supplier-operation-column");
        // 供应商旧 Vue 表格的操作列只有两个链接；其余列表仍保留三项操作的公共最小宽度。
        const operationWidth = supplierOperation
            ? 134
            : compactOperation
              ? Number.isFinite(declaredWidth)
                  ? declaredWidth
                  : 134
              : Number.isFinite(declaredWidth)
                ? Math.max(declaredWidth, 188)
                : 188;

        const render = column.render as (scope: unknown) => ReactNode;
        return {
            ...column,
            width: operationWidth,
            className: column.className ?? "admin-table-operation-column",
            labelClassName: column.labelClassName ?? "admin-table-operation-column",
            render: (scope: unknown) => normalizeOperationActions(render(scope))
        } as T;
    });
}

/** 将旧 Vue 列配置转换为 zhihao-ui-react 的列配置。 */
export function toReactTableColumn<T extends Record<string, any>>(
    column: ColumnProps<T>,
    enumMap: Map<string, any[]> = new Map()
): Record<string, any> {
    const children = column._children?.map(child => toReactTableColumn(child, enumMap));
    const prop = String(column.prop ?? "");
    const enumData = enumMap.get(prop);
    const isOperationColumn = prop === "operation" || prop === "operations" || column.label === "操作";
    const declaredWidth = Number(column.width);
    const compactOperation = String(column.className ?? "").includes("compact");
    const operationWidth = isOperationColumn
        ? compactOperation
            ? Number.isFinite(declaredWidth)
                ? declaredWidth
                : 134
            : Number.isFinite(declaredWidth)
              ? Math.max(declaredWidth, 188)
              : 188
        : column.width;

    return {
        ...column,
        ...(isOperationColumn ? { width: operationWidth } : {}),
        ...(isOperationColumn
            ? {
                  className: column.className ?? "admin-table-operation-column",
                  labelClassName: column.labelClassName ?? "admin-table-operation-column"
              }
            : {}),
        children,
        prop: column.prop,
        isShow: typeof column.isShow === "object" ? Boolean(column.isShow.value) : (column.isShow ?? true),
        isCustomDisable: column.isCustomDisable,
        showOverflowTooltip: column.showOverflowTooltip ?? prop !== "operation",
        className: column.wrap ? "table-column--wrap" : undefined,
        render: (scope: any) => {
            const legacyScope: RenderScope<T> = {
                row: scope.row,
                $index: scope.rowIndex ?? scope.$index ?? 0,
                column: scope.column,
                ...scope
            };
            const value = handleRowAccordingToProp(scope.row, prop);
            if (column.render) {
                const rendered = column.render(legacyScope);
                return isOperationColumn ? normalizeOperationActions(rendered) : rendered;
            }
            if (column.tag) {
                const tagType = enumData ? filterEnum(value, enumData, column.fieldNames, "tag") : undefined;
                return <Tag type={tagType as any}>{renderCellData(column, value, enumData)}</Tag>;
            }
            if (column.click) {
                const formatter = column.formatter as unknown as
                    | ((row: T, column: unknown, value: unknown, index: number) => ReactNode)
                    | undefined;
                return (
                    <span className="link-name" onClick={() => column.click?.(scope.row)}>
                        {formatter
                            ? formatter(scope.row, scope.column, value, legacyScope.$index)
                            : renderCellData(column, value, enumData)}
                    </span>
                );
            }
            const formatter = column.formatter as unknown as
                | ((row: T, column: unknown, value: unknown, index: number) => ReactNode)
                | undefined;
            return (
                <span
                    className={enumData ? filterEnumObj(value, enumData, column.fieldNames)?.state || "text" : "text"}
                >
                    {formatter
                        ? formatter(scope.row, scope.column, value, legacyScope.$index)
                        : renderCellData(column, value, enumData)}
                </span>
            );
        },
        renderHeader: column.headerRender
            ? (scope: any) => column.headerRender?.({ ...scope, $index: scope.columnIndex ?? 0 })
            : undefined
    };
}

/** 保留一个可直接嵌入 JSX 的列声明组件，实际列由父级 Table 统一托管。 */
export function TableColumn<T extends Record<string, any>>({ column, enumMap }: TableColumnProps<T>) {
    const normalized = toReactTableColumn(column, enumMap);
    return <ZhTable.Column {...normalized} />;
}

function renderCellData<T extends Record<string, any>>(column: ColumnProps<T>, value: unknown, enumData?: any[]) {
    return enumData && (column.isFilterEnum ?? true)
        ? filterEnum(value, enumData, column.fieldNames)
        : formatValue(value);
}

export default TableColumn;
